import type {
  VoiceTransport,
  VoiceTransportProblem,
  VoiceTransportState,
} from './transport';

/**
 * `@synquic/slide-voice`, wearing this app's `VoiceTransport` interface.
 *
 * ---------------------------------------------------------------------------
 * WHY AN ADAPTER RATHER THAN A DIRECT IMPORT
 * ---------------------------------------------------------------------------
 * The seam was written before the SDK existed, against the shape the guide
 * described. The SDK that shipped is a different shape - a `SynquicVoiceCall`
 * class with `join`/`setMuted`/`leave` and an event emitter, not a factory
 * returning a transport. Both are reasonable; they are simply not the same.
 *
 * Adapting here rather than rewriting the seam keeps the choice reversible and
 * keeps the provider, the overlays and the call screens ignorant of the
 * vendor - which is the property that made it cheap to integrate a package
 * nobody had seen.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS CHECKS FOR WebRTC BEFORE CLAIMING TO WORK
 * ---------------------------------------------------------------------------
 * `@synquic/slide-voice` is the signalling client. The media stack it needs on
 * React Native is a separate package, `@synquic/slide-voice-native`, which
 * `src/voice/register-native.ts` registers at startup.
 *
 * The check stays because the failure it catches is invisible. If the media
 * stack is missing, or was registered too late, or the build shipped without a
 * native rebuild, the call client concludes the device has no media support -
 * and then behaves impeccably. It connects, rings, is answered, times
 * correctly and ends correctly, carrying no sound, with nothing thrown and
 * nothing logged. The vendor's own changelog describes exactly this happening
 * to their 0.1.0 release.
 *
 * So `isAvailable` is gated on `RTCPeerConnection` genuinely existing rather
 * than on the import succeeding, which keeps the property the stub was built
 * for: **the UI never draws a live call over a dead line.** It says "voice
 * engine not installed" instead, which is at least true and actionable.
 */

/**
 * How the vendor says the microphone did not come up.
 *
 * `@synquic/slide-voice` joins the room, then enables the microphone, and when
 * that second step throws it emits an `error` carrying
 * "Joined, but the microphone could not be enabled: <cause>". There is no code
 * on the event to branch on, so the message is the only signal there is.
 *
 * Matching a vendor string is brittle and worth saying so. It fails in the
 * safe direction: if the wording changes, the notice stops appearing and the
 * call behaves exactly as it does today - one-way, but working - rather than
 * anything breaking. The match is loose for the same reason.
 */
const MICROPHONE_FAILED = /microphone could not be enabled/i;
/** The subset of the vendor's class this adapter drives. */
interface VendorCall {
  join(session: {
    token: string;
    voiceServerUrl: string;
    callId?: string;
  }): Promise<void>;
  setMuted(muted: boolean): Promise<boolean>;
  /** Added in @synquic/slide-voice 0.2.0. Resolves false when unhonoured. */
  setAudioOutput?(output: 'earpiece' | 'speaker'): Promise<boolean>;
  leave(): Promise<void>;
  on(event: string, listener: (...args: unknown[]) => void): unknown;
}

interface VendorModule {
  SynquicVoiceCall?: new () => VendorCall;
  default?: { SynquicVoiceCall?: new () => VendorCall };
}

/**
 * Whether the JS runtime can actually carry a peer connection.
 *
 * `RTCPeerConnection` is installed as a global by the React Native WebRTC
 * native module - it does not exist in bare Hermes. Checking the global rather
 * than trying to require a package name means this works whichever wrapper
 * Synquic supplies.
 */
function hasWebRtcRuntime(): boolean {
  return (
    typeof (globalThis as { RTCPeerConnection?: unknown }).RTCPeerConnection ===
    'function'
  );
}

/**
 * Build a transport around one vendor call object.
 *
 * A fresh `SynquicVoiceCall` per connection rather than one reused across
 * calls: `ended` is documented to fire exactly once, so a reused instance
 * would go quiet after the first hang-up.
 */
export function createVendorTransport(
  CallClass: new () => VendorCall,
): VoiceTransport {
  const listeners = new Set<
    (state: VoiceTransportState, detail?: string) => void
  >();

  /* Kept apart from `listeners` deliberately: these are events rather than
     a state, so there is nothing to replay and no last value to hold. */
  const problems = new Set<(problem: VoiceTransportProblem) => void>();

  let state: VoiceTransportState = 'idle';
  let call: VendorCall | null = null;

  const emit = (next: VoiceTransportState, detail?: string): void => {
    state = next;
    listeners.forEach(listener => listener(next, detail));
  };

  return {
    isAvailable: true,

    async connect(input) {
      emit('connecting');

      const active = new CallClass();
      call = active;

      active.on('connected', () => {
        // Only the audio link. Whether the CALL is answered is the server's
        // fact, and the provider keeps the two apart deliberately.
        if (call === active) emit('connected');
      });

      active.on('ended', (...args: unknown[]) => {
        if (call !== active) return;
        const reason = typeof args[0] === 'string' ? args[0] : 'remote';
        // `disconnected` means the connection dropped and could not be
        // recovered - a failure. `local` and `remote` are somebody hanging up,
        // which is an ordinary close.
        emit(reason === 'disconnected' ? 'failed' : 'closed', reason);
      });

      active.on('error', (...args: unknown[]) => {
        if (call !== active) return;
        const error = args[0];
        const detail = error instanceof Error ? error.message : 'engine error';

        // Not necessarily fatal - the vendor is explicit that a microphone
        // which fails to publish leaves a call you can hear but not speak on.
        // So an error after we are connected is reported and the state held;
        // one before means the join never landed.
        console.error(`[voice] engine error: ${detail}`);
        if (state !== 'connected') {
          emit('failed', detail);
          return;
        }

        /*
         * Held, and now also SAID.
         *
         * This branch used to end at the `console.error` above, which a
         * release build routes nowhere. The result on an iPhone was the worst
         * shape a bug can take: the customer taps "Don't Allow" on the
         * microphone prompt, the call connects, the screen shows a perfectly
         * ordinary live call, and the professional hears silence. Nobody on
         * either end is told why, and the customer has no reason to suspect
         * the prompt they dismissed a second ago.
         *
         * Android almost never reaches here - `ensureMicrophone` asks before
         * the call is placed and will not place one without a grant - which is
         * exactly why this went unnoticed while only Android shipped.
         */
        if (MICROPHONE_FAILED.test(detail)) {
          problems.forEach(listener => listener('microphone-unavailable'));
        }
      });

      try {
        await active.join({
          token: input.token,
          voiceServerUrl: input.voiceServerUrl,
          callId: input.callId,
        });
      } catch (error) {
        const detail =
          error instanceof Error ? error.message : 'could not join the call';
        console.error(`[voice] join failed: ${detail}`);
        call = null;
        emit('failed', detail);
        throw error;
      }
    },

    async disconnect() {
      const active = call;
      call = null;
      if (!active) {
        emit('closed');
        return;
      }
      try {
        await active.leave();
      } catch (error) {
        // Safe to call twice by contract, so a throw here is not worth
        // failing a hang-up over - but it is worth seeing.
        console.error(
          `[voice] leave failed: ${
            error instanceof Error ? error.message : 'unknown error'
          }`,
        );
      }
      emit('closed');
    },

    async setMuted(muted) {
      if (!call) return;
      try {
        await call.setMuted(muted);
      } catch (error) {
        console.error(
          `[voice] setMuted(${muted}) failed: ${
            error instanceof Error ? error.message : 'unknown error'
          }`,
        );
      }
    },

    async setSpeakerphone(on) {
      /*
       * Two different absences, and the old single check conflated them -
       * expensively. A toggle arriving after the call has ended (`call` is
       * null) used to print the same "upgrade your SDK" warning as a build
       * genuinely still on 0.1.x, and that message sent a debugging session
       * hunting a stale bundle that was not stale. No call: silence - the
       * toggle has nothing to act on, and that is not news. A call without
       * the method: the honest no-op warning, because that IS the 0.1.x
       * build, where a speaker button that does nothing quietly beats a
       * call that crashes when it is pressed.
       */
      if (!call) return;

      if (!call.setAudioOutput) {
        console.warn(
          `[voice] setSpeakerphone(${on}) ignored - this build's Synquic SDK ` +
            'has no audio routing. Upgrade to @synquic/slide-voice ^0.2.0.',
        );
        return;
      }

      try {
        const applied = await call.setAudioOutput(on ? 'speaker' : 'earpiece');
        // The vendor returns false when the platform refused rather than
        // throwing, so a route that did not happen is worth a line - otherwise
        // it looks to the user like the button is broken and to us like it
        // worked.
        if (!applied) {
          console.warn(
            `[voice] the platform did not honour setAudioOutput(${
              on ? 'speaker' : 'earpiece'
            })`,
          );
        }
      } catch (error) {
        console.error(
          `[voice] setSpeakerphone(${on}) failed: ${
            error instanceof Error ? error.message : 'unknown error'
          }`,
        );
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },

    subscribeProblems(listener) {
      problems.add(listener);
      return () => {
        problems.delete(listener);
      };
    },
  };
}

/**
 * The vendor transport, or null when this build cannot carry audio.
 *
 * Null for either reason - package absent, or WebRTC runtime absent - because
 * the caller's response is the same in both cases and the distinction belongs
 * in the log rather than in the control flow.
 */
export function resolveVendorTransport(): VoiceTransport | null {
  /*
   * Asked before the require, not after, and the order earns its place.
   *
   * A package that cannot work on this runtime is not worth evaluating, and
   * for one release it was worth much less than that: `@synquic/slide-voice`
   * 0.1.0 bundled the browser `adapter.js`, whose `extends DOMException` threw
   * a ReferenceError under Hermes while Metro was still loading the module.
   * The `catch` below caught it and returned null exactly as written - and the
   * app died anyway, because Metro also hands a module that threw during
   * evaluation to the global error handler, which a release build turns into a
   * fatal a few frames later. Not loading it was the only thing that worked.
   *
   * 0.2.0 dropped the browser shim, so that particular crash is gone. The
   * order stays: a check that costs nothing and is right either way is not
   * worth undoing on the strength of a vendor's current build.
   */
  if (!hasWebRtcRuntime()) {
    console.warn(
      '[voice] No WebRTC runtime (no global RTCPeerConnection), so ' +
        '@synquic/slide-voice is not loaded. Either @synquic/slide-voice-native ' +
        'is missing from this build or it failed to register - see ' +
        'src/voice/register-native.ts. Falling back to the stub so the UI says ' +
        'so rather than drawing a call over a dead line.',
    );
    return null;
  }

  let vendor: VendorModule;

  try {
    // Required, not imported: a static import is resolved by Metro at bundle
    // time, so the app would fail to build for everybody the moment the
    // package were absent - which is precisely the case this file handles.
    vendor = require('@synquic/slide-voice') as VendorModule;
  } catch {
    return null;
  }

  const CallClass =
    vendor?.SynquicVoiceCall ?? vendor?.default?.SynquicVoiceCall;

  if (typeof CallClass !== 'function') {
    console.warn(
      '[voice] @synquic/slide-voice is installed but exports no ' +
        'SynquicVoiceCall. Falling back to the stub.',
    );
    return null;
  }

  return createVendorTransport(CallClass);
}
